import { createContext, ReactNode, useContext, useEffect, useRef } from 'react'

import { Animate } from '@/components/Animate'
import useClickOutside from '@/hooks/useClickOutside'

import s from './Popover.module.css'

const PopoverContext = createContext<{
  isOpen: boolean
  setIsOpen: (open: boolean) => void
} | null>(null)

type PopoverProps = {
  children: ReactNode
  open: boolean
  onOpenChange: (isOpen: boolean) => void
}

type PopoverTriggerProps = {
  children: ReactNode
}

type PopoverContentProps = {
  children: ReactNode
  title?: string
  position?: 'topLeft' | 'topRight' | 'bottomRight'
}

export const Popover = ({
  children,
  open: isOpen,
  onOpenChange: setIsOpen,
}: PopoverProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const setRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false))

  useEffect(() => {
    if (containerRef.current) {
      setRef(containerRef.current)
    }
  }, [setRef])

  return (
    <PopoverContext.Provider value={{ isOpen, setIsOpen }}>
      <div ref={containerRef} className={s.wrapper}>
        {children}
      </div>
    </PopoverContext.Provider>
  )
}

export const PopoverTrigger = ({ children }: PopoverTriggerProps) => {
  const context = useContext(PopoverContext)
  if (!context) throw new Error('PopoverTrigger must be used inside Popover')

  const { isOpen, setIsOpen } = context

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        setIsOpen(!isOpen)
      }}
    >
      {children}
    </div>
  )
}

export const PopoverContent = ({
  children,
  title,
  position = 'bottomRight',
}: PopoverContentProps) => {
  const context = useContext(PopoverContext)
  if (!context) throw new Error('PopoverTrigger must be used inside Popover')

  const { isOpen } = context

  return (
    <Animate
      in={isOpen}
      className={`${s.transition} ${s[position]}`}
      classNameOut={`${s.transitionExit} ${s[position]}`}
    >
      <div
        className={`${s.content} ${s[position]}`}
        role="menu"
        aria-orientation="vertical"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h3 className={s.title}>{title}</h3>}
        {children}
      </div>
    </Animate>
  )
}

Popover.Trigger = PopoverTrigger
Popover.Content = PopoverContent
